<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { getGoodsList } from '/api/index.js'

const props = defineProps({
  categoryData: {
    type: Array,
    default: () => []
  },
  footer: {
    type: Object,
    default: () => ({})
  }
})

const commodityId = ref(props.categoryData[0]?.id || 0)
const goodsMap = ref(new Map())
const loading = ref(false)
const loaded = ref(false)

const goodsList = computed(() => {
  return goodsMap.value.get(Number(commodityId.value)) || []
})

const popularGoods = computed(() => {
  return goodsList.value.slice(0, 2)
})

const normalGoods = computed(() => {
  return goodsList.value.slice(2)
})

const productConfig = computed(() => [
  {
    label: 'CPU',
    field: 'cpu',
    unit: ''
  },
  {
    label: 'index.memory',
    field: 'ram',
    unit: 'G'
  },
  {
    label: 'index.systemDisk',
    field: 'rom',
    unit: 'G'
  },
  {
    label: 'index.dataDisk',
    field: 'data_rom',
    unit: 'G'
  },
  {
    label: 'index.bandwidth',
    field: 'internet',
    unit: 'M'
  },
  {
    label: 'index.defense',
    field: 'defense',
    unit: 'G'
  }
])

const fetchGoodsList = async () => {
  if (loaded.value) return

  loading.value = true

  try {
    const res = await getGoodsList({
      limit: 1000,
      orderby: 'id',
      sort: 'asc'
    })

    const list = res?.data?.list || []
    const map = new Map()

    for (const item of list) {
      const typeid = Number(item.typeid)

      if (!map.has(typeid)) {
        map.set(typeid, [])
      }

      map.get(typeid).push(item)
    }

    goodsMap.value = map
    loaded.value = true
  } catch (error) {
    console.error('获取商品列表失败:', error)
    goodsMap.value = new Map()
  } finally {
    loading.value = false
  }
}


const productSwitching = (id, e) => {
  commodityId.value = id

  e?.currentTarget?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center'
  })
}

watch(
  () => props.categoryData,
  (newVal) => {
    if (!commodityId.value && newVal.length) {
      commodityId.value = newVal[0].id
    }
  },
  {
    immediate: true
  }
)

onMounted(() => {
  fetchGoodsList()
})
</script>

<template>
  <div class="Heziyun-commodityTwo">
    <div class="Heziyun-defaultWidth">
      <div class="Heziyun-commodity-title">
        <span v-intersect="{ class: 'bottom-top', delay: 0 }">
          {{ $t('index.shop3') }} {{ footer?.webname }} - {{ $t('index.shop4') }}
        </span>

        <p v-intersect="{ class: 'bottom-top', delay: 100 }">
          {{ $t('index.shop5') }}{{ footer?.webname }}{{ $t('index.shop6') }}
          <a href="/cart">
            {{ $t('index.viewAllProducts') }}
          </a>
        </p>
      </div>

      <div class="Heziyun-productList">
        <div class="Heziyun-list">
          <div
            class="Heziyun-itemButton"
            v-for="(item, index) in categoryData"
            :key="item.id"
            :class="{ 'Heziyun-select': item.id === commodityId }"
            @click="productSwitching(item.id, $event)"
            v-intersect="{ class: 'right-left', delay: index * 20 }"
          >
            <img v-if="item.img" :src="item.img" alt="" />
            <span class="Heziyun-textOverflow">{{ item.title }}</span>
          </div>
        </div>
      </div>
    </div>

    <a-spin :loading="loading" tip="加载中...">
      <div class="Heziyun-mainBodyCommodity Heziyun-padding">
        <div class="Heziyun-defaultWidth">
          <template v-if="goodsList.length">
            <div class="Heziyun-popular" v-if="popularGoods.length">
              <div
                class="Heziyun-item"
                v-for="(item, index) in popularGoods"
                :key="item.id"
                v-intersect="{ class: 'bottom-top', delay: index * 10 }"
              >
                <span>{{ item.title }}</span>
                <p>{{ item.tips }}</p>

                <div class="Heziyun-toConfigure">
                  <div
                    class="Heziyun-setUp"
                    v-for="config in productConfig"
                    :key="config.field"
                  >
                    <div class="Heziyun-title">
                      {{ config.label === 'CPU' ? 'CPU' : $t(config.label) }}
                    </div>
                    <p>
                      {{ item[config.field] }}{{ config.field === 'cpu' ? $t('index.nucleus') : config.unit }}
                    </p>
                  </div>
                </div>

                <div class="Heziyun-buttones">
                  <a :href="item.url">{{ $t('index.shopNow') }}</a>
                  <a :href="item.url">{{ $t('index.goImmediately') }}</a>
                </div>
              </div>
            </div>

            <div class="Heziyun-convention" v-if="normalGoods.length">
              <div
                class="Heziyun-itemServer"
                v-for="(item, index) in normalGoods"
                :key="item.id"
                v-intersect="{ class: 'bottom-top', delay: index * 10 }"
              >
                <div class="Heziyun-title">{{ item.title }}</div>
                <div class="Heziyun-describe">{{ item.tips }}</div>

                <div class="Heziyun-toConfigure">
                  <div
                    class="Heziyun-setUp"
                    v-for="config in productConfig"
                    :key="config.field"
                  >
                    <span>
                      {{ config.label === 'CPU' ? 'CPU' : $t(config.label) }}
                    </span>
                    <p>
                      {{ item[config.field] }}{{ config.field === 'cpu' ? $t('index.nucleus') : config.unit }}
                    </p>
                  </div>
                </div>

                <div class="Heziyun-amountOfMoney">
                  <div class="Heziyun-price">
                    <i>¥{{ item.money }}</i>/{{ $t('index.month') }}
                  </div>
                  <a :href="item.url">{{ $t('index.buyNow') }}</a>
                </div>
              </div>
            </div>
          </template>

          <div class="Heziyun-noDataAvailable" v-else>
            <img src="@/static/img/index/wuData.png" alt="" />
            <span>{{ $t('index.noDataAvailable') }}</span>
          </div>
        </div>
      </div>
    </a-spin>
  </div>
</template>

<style lang="less" scoped>
:deep(.arco-spin) {
  width: 100%;
}

.Heziyun-commodityTwo {
  background-color: #fff;
  padding-top: 3.5rem;

  .Heziyun-defaultWidth {
    .Heziyun-productList {
      .Heziyun-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
        gap: 1rem;
        padding-bottom: 0.3rem;
        font-family: "思源黑";

        .Heziyun-itemButton {
          max-width: max-content;
          padding: 0.4rem 0.5rem;
          position: relative;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.1rem;

          &:hover {
            color: #0052d9;
          }

          &::before {
            content: "";
            width: 0;
            height: 2px;
            background-color: #0052d9;
            position: absolute;
            bottom: -0.2rem;
            left: 0;
            transition: width 0.3s ease-in-out;
          }

          img {
            width: 2em;
            height: 1rem;
            object-fit: contain;
          }
        }

        .Heziyun-select {
          color: #0052d9;

          &::before {
            width: 100%;
          }
        }
      }
    }
  }

  .Heziyun-mainBodyCommodity {
    position: relative;
    padding: 3.5rem 0;
    background: #f3f5f8;
    box-shadow: inset 8px 8px 20px 0 rgba(55, 99, 170, 0.11),
      inset -8px -8px 20px 0 #fff;

    .vs-loading--target {
      background-color: rgb(249 249 249) !important;
    }

    .Heziyun-defaultWidth {
      display: flex;
      align-items: flex-start;
      gap: 1rem;

      .Heziyun-popular {
        flex: 0.3;
        display: flex;
        flex-direction: column;
        gap: 1rem;

        .Heziyun-item {
          background: url('@/static/img/index/commodityTwo.webp') no-repeat center;
          background-size: cover;
          color: #fff;
          padding: 1rem;
          border: 2px solid #fff;
          box-shadow: 8px 8px 20px 0 rgba(55, 99, 170, 0.1),
            -8px -8px 20px 0 #fff;
          border-radius: 4px;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;

          span {
            font-size: 1.2rem;
            text-shadow: 0 1px 0 rgb(0 101 190);
            font-family: "思源黑";
          }

          p {
            font-size: 0.82rem;
            color: #fff;
            text-shadow: 0 1px 0 rgb(0 0 0 / 42%);
          }

          .Heziyun-toConfigure {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.4rem;

            .Heziyun-setUp {
              width: calc(50% - 0.2rem);
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: space-between;
              background: rgb(255 255 255 / 15%);
              padding: 0.3rem;

              .Heziyun-title {
                color: #fff;
                font-size: 0.75rem;
                text-shadow: 0 1px 0 rgba(0, 0, 0, 0.42);
              }

              p {
                color: #fff;
                font-size: 0.8rem;
                font-weight: bold;
              }
            }
          }

          .Heziyun-buttones {
            display: flex;
            gap: 0.4rem;
          }

          a {
            background-color: #007aff;
            padding: 0.5rem 2rem;
            color: #fff;
            box-shadow: 1px 2px 4px #0000006b;
            width: calc(50% - 0.2rem);
            text-align: center;

            &:hover {
              background-color: #5a98ff;
            }

            &:nth-child(2) {
              background-color: #fff;
              color: #43474d;

              &:hover {
                background-color: #e9e9e9;
              }
            }
          }
        }
      }

      .Heziyun-convention {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.1rem;
        flex: 0.7;

        .Heziyun-itemServer {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.8rem;
          max-height: fit-content;
          flex-shrink: 0;
          padding: 1rem;
          background: linear-gradient(0deg, rgba(0, 0, 0, 0.001), rgba(0, 0, 0, 0.001)), #fff;
          transition: all 0.4s ease;

          &:hover {
            z-index: 1;
            box-shadow: 3px 3px 12px rgba(55, 99, 170, 0.2);
          }

          .Heziyun-title {
            font-size: 1rem;
          }

          .Heziyun-describe {
            color: #6b7280;
            font-size: 0.7rem;
          }

          .Heziyun-toConfigure {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.4rem;
            width: 100%;

            .Heziyun-setUp {
              width: calc((100% / 3) - 0.4rem);
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: space-between;
              background: #f9fafb;
              padding: 0.3rem;

              span {
                color: #4b5563;
                font-size: 0.75rem;
              }

              p {
                color: #3176ff;
                font-size: 0.8rem;
                font-weight: bold;
              }
            }
          }

          .Heziyun-amountOfMoney {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;

            .Heziyun-price {
              color: #6b7280;
              font-size: 0.8rem;

              i {
                font-style: normal;
                color: #3176ff;
                font-size: 1.2rem;
                font-weight: bold;
              }
            }

            a {
              background: #3176ff;
              color: #fff;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 5rem;
              height: 1.9rem;
              box-shadow: 0 4px 16px rgba(11, 83, 255, 0.24);

              &:hover {
                opacity: 0.75;
              }
            }
          }
        }
      }

      .Heziyun-noDataAvailable {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 3rem 0;
      }
    }
  }
}

@media (max-width: 768px) {
  .Heziyun-commodityTwo {
    padding-top: 3.5vw;

    .Heziyun-defaultWidth {
      .Heziyun-productList {
        .Heziyun-list {
          display: flex;
          gap: 3vw;
          padding-bottom: 1vw;
          overflow-x: auto;

          &::-webkit-scrollbar {
            display: none;
          }

          .Heziyun-itemButton {
            padding: 3vw;
            gap: 2vw;
            font-size: 4vw;
            flex-shrink: 0;

            img {
              width: 8vw;
              height: 7vw;
            }
          }
        }
      }
    }

    .Heziyun-mainBodyCommodity {
      padding: 5vw 0;

      .Heziyun-defaultWidth {
        gap: 4vw;
        flex-direction: column;

        .Heziyun-popular {
          flex: 1;
          gap: 4vw;
          width: 100%;

          .Heziyun-item {
            padding: 4vw;
            gap: 3vw;

            span {
              font-size: 5vw;
            }

            p {
              font-size: 3.5vw;
            }

            .Heziyun-toConfigure {
              gap: 2vw;

              .Heziyun-setUp {
                width: calc(50% - 2vw);
                padding: 2vw;

                .Heziyun-title,
                p {
                  font-size: 3.5vw;
                }
              }
            }

            .Heziyun-buttones {
              gap: 2vw;
            }

            a {
              padding: 3vw;
              width: calc(50% - 2vw);
              font-size: 3.5vw;
            }
          }
        }

        .Heziyun-convention {
          grid-template-columns: repeat(1, 1fr);
          gap: 3vw;
          flex: 1;
          width: 100%;

          .Heziyun-itemServer {
            gap: 2vw;
            padding: 3vw;

            .Heziyun-title {
              font-size: 4.5vw;
            }

            .Heziyun-describe {
              font-size: 3.5vw;
            }

            .Heziyun-toConfigure {
              gap: 2vw;

              .Heziyun-setUp {
                width: calc(50% - 1vw);
                padding: 3vw;

                span,
                p {
                  font-size: 3.5vw;
                }
              }
            }

            .Heziyun-amountOfMoney {
              .Heziyun-price {
                font-size: 3.5vw;

                i {
                  font-size: 5vw;
                }
              }

              a {
                width: 25vw;
                height: 9vw;
                font-size: 3.5vw;
              }
            }
          }
        }

        .Heziyun-noDataAvailable {
          padding: 6vw 0;
        }
      }
    }
  }
}
</style>